import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  fetchAdminAssistedDispatchQueue,
  fetchAdminTrips,
  fetchAdminTripsSummary,
  sendAdminWhatsAppMessage,
} from '../services/api/admin';
import type { AdminAssistedDispatchQueueItem, AdminTripListItem, AdminTripsSummary } from '../types/admin';
import {
  paymentOpsByPrimaryBookingUrl,
  paymentOpsByProviderIntentUrl,
  paymentOpsByTripGroupUrl,
  providerEventsByReferenceUrl,
} from '../utils/adminPaymentOpsLinks';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATUS_ORDER = [
  'PENDING',
  'ASSIGNED',
  'ACCEPTED',
  'ON_ROUTE_TO_PICKUP',
  'LOADING',
  'PICKED_UP',
  'ENROUTE',
  'UNLOADING',
  'DELIVERED',
  'COMPLETED',
  'EXPIRED',
  'REJECTED',
  'CANCELLED',
  'CANCELLED_BY_SYSTEM',
];

const STATUS_CLASS_MAP: Record<string, string> = {
  COMPLETED: 'admin-pill-success',
  DELIVERED: 'admin-pill-success',
  ACCEPTED: 'admin-pill-active',
  ON_ROUTE_TO_PICKUP: 'admin-pill-active',
  ENROUTE: 'admin-pill-active',
  PICKED_UP: 'admin-pill-active',
  LOADING: 'admin-pill-active',
  UNLOADING: 'admin-pill-active',
  CANCELLED: 'admin-pill-danger',
  CANCELLED_BY_SYSTEM: 'admin-pill-danger',
  REJECTED: 'admin-pill-danger',
  EXPIRED: 'admin-pill-warning',
  PENDING: 'admin-pill-warning',
  ASSIGNED: 'admin-pill-warning',
};

function formatStatusLabel(status: string | null | undefined): string {
  return (status || 'UNKNOWN').replaceAll('_', ' ');
}

function formatCompactNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA').format(Number(value ?? 0));
}

function formatDateForInput(value: string | null): string {
  if (!value) return '';
  return value;
}

function isWhatsAppSource(source?: string | null): boolean {
  return source === 'WHATSAPP_ASSISTED_BOOKING';
}

function formatSourceLabel(source?: string | null): string {
  if (isWhatsAppSource(source)) return 'WhatsApp';
  return source ? source.replaceAll('_', ' ') : 'Standard';
}

function defaultWhatsAppAdminMessage(item: AdminAssistedDispatchQueueItem): string {
  const reference = item.bookingId || item.tripId;
  return [
    `Hi ${item.requesterName || 'there'}, this is ShiftWagon support.`,
    `We received your booking${reference ? ` ${reference}` : ''} and are arranging a driver for you.`,
    'We will update you as soon as dispatch is confirmed.',
  ].join(' ');
}

const AdminTripsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<AdminTripsSummary | null>(null);
  const [trips, setTrips] = useState<AdminTripListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [assistedQueue, setAssistedQueue] = useState<AdminAssistedDispatchQueueItem[]>([]);
  const [assistedTotal, setAssistedTotal] = useState(0);
  const [assistedLoading, setAssistedLoading] = useState(true);
  const [messageTarget, setMessageTarget] = useState<AdminAssistedDispatchQueueItem | null>(null);
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageNotice, setMessageNotice] = useState('');
  const [messageError, setMessageError] = useState('');

  const filters = useMemo(
    () => ({
      status: searchParams.get('status') || '',
      query: searchParams.get('query') || '',
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
      flow: searchParams.get('flow') || '',
      paymentStatus: searchParams.get('paymentStatus') || '',
      userId: searchParams.get('userId') || '',
      driverId: searchParams.get('driverId') || '',
      page: Number(searchParams.get('page') || '0'),
      size: Number(searchParams.get('size') || '12'),
      sort: searchParams.get('sort') || 'createdAt,desc',
    }),
    [searchParams],
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryResponse, listResponse] = await Promise.all([
          fetchAdminTripsSummary(),
          fetchAdminTrips({
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.query ? { query: filters.query.trim() } : {}),
            ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
            ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
            ...(filters.flow ? { flow: filters.flow } : {}),
            ...(filters.paymentStatus ? { paymentStatus: filters.paymentStatus } : {}),
            ...(filters.userId ? { userId: filters.userId.trim() } : {}),
            ...(filters.driverId ? { driverId: filters.driverId.trim() } : {}),
            page: filters.page,
            size: filters.size,
            sort: filters.sort,
          }),
        ]);

        if (!mounted) return;
        setSummary(summaryResponse);
        setTrips(listResponse.items);
        setTotalPages(listResponse.totalPages);
        setTotalElements(listResponse.totalElements);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load admin trips.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters]);

  useEffect(() => {
    let mounted = true;

    const loadAssistedQueue = async () => {
      setAssistedLoading(true);
      try {
        const response = await fetchAdminAssistedDispatchQueue({
          page: 0,
          size: 6,
          bookingSource: 'WHATSAPP_ASSISTED_BOOKING',
          adminDispatchRequired: true,
        });
        if (!mounted) return;
        setAssistedQueue(response.items);
        setAssistedTotal(response.totalElements);
      } catch {
        if (!mounted) return;
        setAssistedQueue([]);
        setAssistedTotal(0);
      } finally {
        if (mounted) setAssistedLoading(false);
      }
    };

    void loadAssistedQueue();
    return () => {
      mounted = false;
    };
  }, []);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set('page', '0');
    setSearchParams(next);
  };

  const updatePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
  };

  const openWhatsAppMessage = (item: AdminAssistedDispatchQueueItem) => {
    setMessageTarget(item);
    setMessageBody(defaultWhatsAppAdminMessage(item));
    setMessageNotice('');
    setMessageError('');
  };

  const closeWhatsAppMessage = () => {
    if (messageSending) return;
    setMessageTarget(null);
    setMessageBody('');
    setMessageError('');
  };

  const submitWhatsAppMessage = async () => {
    if (!messageTarget?.requesterPhone) {
      setMessageError('This WhatsApp booking has no customer phone number.');
      return;
    }

    if (!messageBody.trim()) {
      setMessageError('Enter a message before sending.');
      return;
    }

    setMessageSending(true);
    setMessageError('');
    setMessageNotice('');
    try {
      await sendAdminWhatsAppMessage({
        to: messageTarget.requesterPhone,
        body: messageBody.trim(),
      });
      setMessageNotice('WhatsApp message queued for delivery.');
      setMessageTarget(null);
      setMessageBody('');
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the WhatsApp message.');
    } finally {
      setMessageSending(false);
    }
  };

  return (
    <AdminShell
      badge="Trips"
      copy="Monitor trip-level booking operations, follow the effective status per trip, and drill into full booking-row detail when needed."
      title="Admin trips"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <div className="admin-grid admin-grid-cards" style={{ marginBottom: '18px' }}>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Total trips</span>
          <strong>{formatCompactNumber(summary?.totalTrips)}</strong>
          <span className="admin-stat-note">All trip-level records currently tracked by the admin summary.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Active trips</span>
          <strong>{formatCompactNumber(summary?.activeTrips)}</strong>
          <span className="admin-stat-note">Trips currently still in flight across assignment or delivery stages.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Completed trips</span>
          <strong>{formatCompactNumber(summary?.completedTrips)}</strong>
          <span className="admin-stat-note">Trips that reached the final completed state.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Cancelled trips</span>
          <strong>{formatCompactNumber(summary?.cancelledTrips)}</strong>
          <span className="admin-stat-note">Trips cancelled by user, driver, or the system.</span>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Trips by status</h2>
            <p className="admin-panel-copy">These counts come directly from the backend’s trip-level aggregation. The main list below stays one row per trip.</p>
          </div>
        </div>
        <div className="admin-pill-row">
          {STATUS_ORDER.map((status) => (
            <button
              className={`admin-pill admin-pill-button ${filters.status === status ? 'admin-pill-button-active' : ''} ${STATUS_CLASS_MAP[status] || ''}`}
              key={status}
              onClick={() => updateFilter('status', filters.status === status ? '' : status)}
              type="button"
            >
              {formatStatusLabel(status)}: {formatCompactNumber(summary?.countsByStatus?.[status] ?? 0)}
            </button>
          ))}
        </div>
      </section>

      <section className="admin-panel" style={{ marginTop: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">WhatsApp admin dispatch</h2>
            <p className="admin-panel-copy">Confirmed WhatsApp bookings with no nearby driver candidates.</p>
          </div>
          <span className="admin-pill admin-pill-warning">
            {assistedLoading ? 'Loading' : `${formatCompactNumber(assistedTotal)} require dispatch`}
          </span>
        </div>
        {messageNotice ? <div className="admin-message-alert">{messageNotice}</div> : null}
        {assistedLoading ? (
          <div className="register-loader" />
        ) : assistedQueue.length ? (
          <div className="admin-whatsapp-dispatch-list">
            {assistedQueue.map((item) => (
              <div className="admin-whatsapp-dispatch-card" key={`${item.tripId}-${item.bookingId}`}>
                <div>
                  <div className="admin-whatsapp-dispatch-title">
                    <strong>{item.bookingId || item.tripId}</strong>
                    <span className="admin-pill admin-pill-whatsapp">WhatsApp</span>
                    {item.adminDispatchRequired ? (
                      <span className="admin-pill admin-pill-warning">Admin dispatch required</span>
                    ) : null}
                  </div>
                  <small className="admin-muted-note">Scheduled: {formatDateTime(item.scheduledAt)}</small>
                  <small className="admin-muted-note">{item.sourceAddress || 'Pickup pending'}</small>
                  <small className="admin-muted-note">{item.destinationAddress || 'Dropoff pending'}</small>
                </div>
                <div>
                  <span className="admin-stat-label">Customer</span>
                  <strong>{item.requesterName || 'WhatsApp customer'}</strong>
                  <small className="admin-muted-note">{item.requesterPhone || 'No WhatsApp number'}</small>
                </div>
                <div>
                  <span className="admin-stat-label">Candidates</span>
                  <strong>{formatCompactNumber(item.driverCandidateCount)}</strong>
                  <small className="admin-muted-note">{item.vehicleType || 'Vehicle pending'}</small>
                </div>
                <div className="admin-table-actions">
                  <Link className="admin-row-link" to={`/admin/trips/${item.tripId}`}>
                    View trip
                  </Link>
                  <button
                    className="admin-payment-action-button"
                    disabled={!item.requesterPhone}
                    onClick={() => openWhatsAppMessage(item)}
                    type="button"
                  >
                    Message customer on WhatsApp
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-empty">No WhatsApp bookings currently require admin dispatch.</p>
        )}
      </section>

      <section className="admin-panel" style={{ marginTop: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Trips list</h2>
            <p className="admin-panel-copy">Filter by status, date window, flow, payment state, or raw booking search fields like trip ID and address.</p>
          </div>
        </div>

        <div className="admin-toolbar admin-toolbar-trips">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => updateFilter('query', event.target.value)}
            placeholder="Search by trip ID, booking ref, user ID, driver ID, or address"
            value={filters.query}
          />
          <select className="register-input" onChange={(event) => updateFilter('status', event.target.value)} value={filters.status}>
            <option value="">All statuses</option>
            {STATUS_ORDER.map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </select>
          <select className="register-input" onChange={(event) => updateFilter('flow', event.target.value)} value={filters.flow}>
            <option value="">All flows</option>
            <option value="INSTANT">Instant</option>
            <option value="ASSIGNMENT">Assignment</option>
          </select>
          <select className="register-input" onChange={(event) => updateFilter('paymentStatus', event.target.value)} value={filters.paymentStatus}>
            <option value="">All payment states</option>
            <option value="PENDING">Pending</option>
            <option value="PAID">Paid</option>
            <option value="REFUNDED">Refunded</option>
          </select>
          <input
            className="register-input"
            onChange={(event) => updateFilter('dateFrom', event.target.value)}
            type="date"
            value={formatDateForInput(filters.dateFrom)}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('dateTo', event.target.value)}
            type="date"
            value={formatDateForInput(filters.dateTo)}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('userId', event.target.value)}
            placeholder="Requester user ID"
            value={filters.userId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('driverId', event.target.value)}
            placeholder="Driver ID"
            value={filters.driverId}
          />
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table admin-table-trips">
              <div className="admin-table-head admin-table-head-trips">
                <span>Trip</span>
                <span>Status</span>
                <span>Schedule</span>
                <span>Route</span>
                <span>Participants</span>
                <span>Money</span>
                <span>Ops</span>
              </div>
              {trips.map((trip) => (
                <div className="admin-table-row admin-table-row-trips" key={trip.tripId}>
                  <span>
                    <Link className="admin-row-link" to={`/admin/trips/${trip.tripId}`}>
                      {trip.representativeBookingId || 'Booking reference pending'}
                    </Link>
                    <small>{trip.tripId}</small>
                    <small>{trip.flow || 'Flow pending'}</small>
                    {trip.bookingSource ? (
                      <span className={`admin-pill ${isWhatsAppSource(trip.bookingSource) ? 'admin-pill-whatsapp' : ''}`}>
                        {formatSourceLabel(trip.bookingSource)}
                      </span>
                    ) : null}
                  </span>
                  <span>
                    <span className={`admin-pill ${STATUS_CLASS_MAP[trip.status || ''] || ''}`}>
                      {formatStatusLabel(trip.status)}
                    </span>
                    <small>{trip.paymentStatus || 'Payment state pending'}</small>
                  </span>
                  <span>
                    <strong>{formatDateTime(trip.createdAt)}</strong>
                    <small>Scheduled: {formatDateTime(trip.scheduledAt)}</small>
                  </span>
                  <span>
                    <strong>{trip.sourceAddress || 'Pickup pending'}</strong>
                    <small>{trip.destinationAddress || 'Dropoff pending'}</small>
                  </span>
                  <span>
                    <strong>{trip.requesterName || 'Requester unavailable'}</strong>
                    <small>{trip.requesterEmail || 'Requester email not available'}</small>
                    {trip.bookingSourcePhone ? <small>WhatsApp: {trip.bookingSourcePhone}</small> : null}
                    <small>{trip.activeDriverName || 'Driver not assigned yet'}</small>
                    <small>{trip.activeDriverEmail || 'Driver email not available'}</small>
                  </span>
                  <span>
                    <strong>{formatCurrency(trip.fareAmount ?? trip.estimatedFare)}</strong>
                    <small>Commission {formatCurrency(trip.commissionAmount)}</small>
                    <small>Payout {formatCurrency(trip.driverPayout)}</small>
                  </span>
                  <span className="admin-table-actions">
                    <small>{trip.vehicleType || 'Vehicle pending'}</small>
                    {trip.driverCandidateCount != null ? (
                      <small>{formatCompactNumber(trip.driverCandidateCount)} driver candidates</small>
                    ) : null}
                    {trip.adminDispatchRequired ? (
                      <span className="admin-pill admin-pill-warning">Admin dispatch required</span>
                    ) : null}
                    {trip.cancelReason ? <small>{trip.cancelReason}</small> : null}
                    {trip.tripGroupId || trip.tripId ? (
                      <Link className="admin-row-link" to={paymentOpsByTripGroupUrl(trip.tripGroupId || trip.tripId)}>
                        Payment Ops
                      </Link>
                    ) : null}
                    {trip.representativeBookingId ? (
                      <Link className="admin-row-link" to={paymentOpsByPrimaryBookingUrl(trip.representativeBookingId)}>
                        Booking payment
                      </Link>
                    ) : null}
                    {trip.paymentRef ? (
                      <Link className="admin-row-link" to={paymentOpsByProviderIntentUrl(trip.paymentRef)}>
                        Payment ref
                      </Link>
                    ) : null}
                    {trip.paymentProvider && trip.paymentRef ? (
                      <Link className="admin-row-link" to={providerEventsByReferenceUrl(trip.paymentProvider, trip.paymentRef)}>
                        Provider events
                      </Link>
                    ) : null}
                  </span>
                </div>
              ))}
              {!trips.length ? <p className="admin-empty">No trips match the current filters.</p> : null}
            </div>

            <div className="admin-pagination">
              <span>{formatCompactNumber(totalElements)} trips</span>
              <button
                className="register-secondary-button"
                disabled={filters.page === 0}
                onClick={() => updatePage(Math.max(filters.page - 1, 0))}
                type="button"
              >
                Previous
              </button>
              <span>
                Page {filters.page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                className="register-secondary-button"
                disabled={filters.page + 1 >= Math.max(totalPages, 1)}
                onClick={() => updatePage(filters.page + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
      {messageTarget ? (
        <div className="admin-modal-backdrop" role="presentation">
          <div aria-modal="true" className="admin-modal" role="dialog">
            <div className="admin-modal-header">
              <div>
                <h2>Message customer on WhatsApp</h2>
                <p>{messageTarget.requesterPhone || 'No phone number available'}</p>
              </div>
              <button
                aria-label="Close WhatsApp message dialog"
                className="admin-modal-close"
                disabled={messageSending}
                onClick={closeWhatsAppMessage}
                type="button"
              >
                x
              </button>
            </div>
            {messageError ? <div className="register-inline-error">{messageError}</div> : null}
            <label className="admin-modal-field">
              <span>Message</span>
              <textarea
                className="register-input"
                maxLength={4096}
                onChange={(event) => setMessageBody(event.target.value)}
                value={messageBody}
              />
            </label>
            <div className="admin-modal-actions">
              <button
                className="register-secondary-button"
                disabled={messageSending}
                onClick={submitWhatsAppMessage}
                type="button"
              >
                {messageSending ? 'Sending...' : 'Send WhatsApp message'}
              </button>
              <button
                className="admin-payment-action-button"
                disabled={messageSending}
                onClick={closeWhatsAppMessage}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminTripsPage;
